/**
 * @typedef {Object} Breakpoints
 * @property {string} xs
 * @property {string} sm
 * @property {string} md
 * @property {string} lg
 * @property {string} xl
 */

/**
 * @see https://material-ui.com/customization/breakpoints/
 * @type {Breakpoints}
 */
export const breakpoints = ['0px', '600px', '960px', '1280px', '1920px'];

/* eslint-disable prefer-destructuring */
breakpoints.xs = breakpoints[0]; // 0px
breakpoints.sm = breakpoints[1]; // 600px
breakpoints.md = breakpoints[2]; // 960px
breakpoints.lg = breakpoints[3]; // 1280px
breakpoints.xl = breakpoints[4]; // 1920px

/**
 * @typedef {array} FontSizes
 * @property {string} body
 * @property {string} h4
 * @property {string} h3
 * @property {string} h2
 * @property {string} h1
 */

/**
 *
 * @type {FontSizes}
 */
export const fontSizes = {
  h1: '44px',
  h1m: '37px',
  h2: '36px',
  h2m: '28px',
  h3: '26px',
  h3m: '24px',
  subtitle: '22px',
  subtitlem: '20px',
  body: '18px',
  bodym: '16px',
  button: '16px',
  menu: '16px',
  caption: '14px',
};

/**
 * Custom theme
 * @see https://rebassjs.org/theming/
 * @see https://rebassjs.org/variants/
 */

const body = {
  fontSize: ['bodym', null, null, 'body'],
  lineHeight: ['bodym', null, null, 'body'],
  m: 0,
};

const h1 = {
  color: 'heading',
  fontSize: ['h1m', null, null, 'h1'],
  lineHeight: ['h1m', null, null, 'h1'],
  fontFamily: 'heading',
  fontWeight: 'heading',
  mt: 0,
  mb: 3,
};

const h2 = {
  color: 'heading',
  fontSize: ['h2m', null, null, 'h2'],
  lineHeight: ['h2m', null, null, 'h2'],
  fontFamily: 'heading',
  fontWeight: 'heading',
  mt: 0,
  mb: 3,
};

const h3 = {
  color: 'heading',
  fontSize: ['h3m', null, null, 'h3'],
  lineHeight: ['h3m', null, null, 'h3'],
  fontFamily: 'heading',
  fontWeight: 'heading',
  mt: 0,
  mb: 3,
};

const subtitle = {
  color: 'heading',
  fontSize: ['subtitlem', null, null, 'subtitle'],
  lineHeight: ['subtitlem', null, null, 'subtitle'],
  fontFamily: 'body',
  mt: 0,
  mb: 3,
};

const caption = {
  color: 'body',
  fontSize: 'caption',
  lineHeight: 'caption',
  fontFamily: 'body',
};

const link = {
  color: 'primary',
  textDecoration: 'none',
  cursor: 'pointer',

  '&:hover': {
    textDecoration: 'underline',
  },

  '&.activeLink': {
    pointerEvents: 'none',
    color: 'secondary.0',
  },
};

const ul = {
  p: 0,
  m: 0,
  listStyle: 'none',

  '& > li': {
    position: 'relative',
    mb: 3,
    pl: 3,
  },

  '& > li::before': {
    position: 'absolute',
    top: 2,
    left: 0,
    content: '""',
    display: 'block',
    width: '4px',
    height: '4px',
    borderRadius: 'circle',
    flexShrink: 0,
    backgroundColor: 'basic.3',
  },
};

const ol = {
  p: 0,
  m: 0,
  listStyle: 'none',
  counterReset: 'listCounter',

  '& > li': {
    position: 'relative',
    mb: 3,
    pl: 4,
    counterIncrement: 'listCounter',
  },

  '& > li::before': {
    position: 'absolute',
    left: 0,
    content: 'counter(listCounter)',
    display: 'block',
    width: '32px',
    flexShrink: 0,
    color: 'basic.3',
  },
};

const theme = {
  breakpoints,
  fontSizes,
  fonts: {
    body: 'Lato, sans-serif',
    heading: 'Lato, sans-serif',
  },
  lineHeights: {
    h1: '54px',
    h1m: '44px',
    h2: '46px',
    h2m: '34px',
    h3: '32px',
    h3m: '26px',
    body: '29px',
    subtitle: '32px',
    subtitlem: '26px',
    bodym: 1.5,
    button: 0,
    menu: 0,
    caption: '21px',
  },
  fontWeights: {
    body: '400',
    heading: '700',
    bold: '700',
  },
  colors: {
    basic: ['#FFF', '#FCFCFC', '#F4F7FA', '#F2F2F2', '#E1E1E1', '#C1C1C1'],
    body: '#7B7B7B',
    heading: '#424242',
    primary: '#0080D1',
    secondary: ['#004695', '#1565C0', '#8CABCA', '#DAE6F2', '#F3F7FA'],
    status: ['#43A047', '#AD8200', '#E94B51'],
    ok: '#43A047',
    warn: '#AD8200',
    error: '#E94B51',
  },
  space: [0, 4, 8, 16, 32, 64],
  radii: {
    small: '3px',
    large: '100px',
    circle: '50%',
  },
  shadows: {
    small: '0px 5px 4px rgba(32, 54, 70, 0.15)',
    medium: '0px 6px 16px rgba(32, 54, 70, 0.16)',
    large: '10px 10px 30px rgba(32, 54, 70, 0.1)',
  },
  variants: {
    link,
    footerLink: {
      display: 'block',
      mb: 3,
      color: 'basic.5',
      textDecoration: 'none',
      fontSize: 'caption',
      lineHeight: 'caption',

      '&:hover': {
        textDecoration: 'underline',
      },

      '&.activeLink': {
        pointerEvents: 'none',
        color: 'basic.0',
        textDecoration: 'underline',
      },
    },
    headerLink: {
      display: 'block',
      color: 'heading',
      textDecoration: 'none',
      position: 'relative',
      fontSize: ['bodym', null, null, 'body'],

      '&::before': {
        content: '""',
        display: 'block',
        height: '2px',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0)',
        transition: 'all .1s cubic-bezier(.4,0,1,1) 0s',
        width: '100%',
      },

      '&:hover': {
        '&::before': {
          backgroundColor: 'primary',
          bottom: '-3px',
        },
      },

      '&.activeLink': {
        pointerEvents: 'none',
        textDecoration: 'underline',
      },
    },
    ul,
    ol,
    content: {
      p: body,
      h1,
      h2,
      h3,
      a: link,
      ol,
      ul,
    },
    card: {
      bg: 'basic.0',
      p: 3,
      boxShadow: 'small',
      transition: 'all 0.3s ease',

      '&:hover': {
        boxShadow: 'large',
      },
    },
  },
  text: {
    body,
    h1,
    h2,
    h3,
    subtitle,
    caption,
  },
  buttons: {
    primary: {
      fontSize: 'button',
      color: 'basic.0',
      fontWeight: 'bold',
      bg: 'primary',
      px: '40px',
      borderRadius: 'large',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',

      '&:hover, &:focus': {
        bg: 'secondary.1',
        outline: 0,
      },

      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
    },
    secondary: {
      fontSize: 'button',
      color: 'primary',
      fontWeight: 'bold',
      bg: 'basic.0',
      px: '40px',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'primary',
      borderRadius: 'large',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',

      '&:hover, &:focus': {
        bg: 'primary',
        outline: 0,
        color: 'basic.0',
      },

      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
    },
    secondaryLight: {
      fontSize: 'button',
      color: 'basic.0',
      fontWeight: 'bold',
      bg: 'rgba(255,255,255,0)',
      px: '40px',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'basic.0',
      borderRadius: 'large',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',

      '&:hover, &:focus': {
        bg: 'basic.0',
        outline: 0,
        color: 'primary',
      },

      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
      },
    },
  },
  forms: {
    label: {
      pb: 2,
    },
    input: {
      p: 2,
      color: 'basic.5',
      backgroundColor: 'basic.0',
      borderColor: 'basic.2',
      outline: 0,

      '&::placeholder': {
        color: 'basic.4',
      },

      '&:focus': {
        borderColor: 'basic.3',
      },

      '&:disabled': {
        position: 'relative',
        zIndex: 1,
        backgroundColor: 'basic.2',
        borderColor: 'basic.3',
      },

      '&[type=number]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '&[type=number]::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },

      '&.error': {
        borderColor: 'error',
      },
    },
    select: {
      p: 2,
      color: 'basic.5',
      backgroundColor: 'basic.0',
      borderColor: 'basic.2',
      outline: 0,

      '& + svg': {
        position: 'absolute',
        top: '10px',
        right: '6px',
        stroke: 'basic.3',
      },

      '&::placeholder': {
        color: 'basic.4',
      },

      '&:disabled': {
        backgroundColor: 'basic.2',
        borderColor: 'basic.3',
      },

      '&:focus': {
        borderColor: 'basic.3',
      },

      '&::-ms-expand': {
        display: 'none',
      },

      '&.error': {
        borderColor: 'error',
      },
    },
    checkbox: {
      width: '24px',
      minWidth: '24px',
      margin: 0,
      mr: '8px',
      padding: 0,
      borderRadius: 0,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'basic.2',
      outline: 0,
      bg: 'basic.0',
      transition: 'border 0.3s',

      '&.error': {
        borderColor: 'error',
      },
      '& svg': {
        stroke: 'basic.2',
        strokeWidth: 3,
        transition: 'stroke 0.3s',
      },
      '&:hover': {
        cursor: 'pointer',
        borderColor: 'primary',

        svg: {
          stroke: 'primary',
        },
      },
      'input:focus ~ &': {
        borderColor: 'primary',

        svg: {
          stroke: 'primary',
        },
      },
      'input:active ~ &': {
        borderColor: 'primary',

        svg: {
          stroke: 'primary',
        },
      },
      'input:disabled:active ~ &': {
        borderWidth: '1px',
        borderColor: 'basic.2',
      },
      'input:disabled ~ &': {
        opacity: 0.5,

        '&:hover': {
          cursor: 'not-allowed',
          borderColor: 'basic.2',
        },
        '& svg': {
          stroke: 'basic.2',
        },
      },
    },
    radio: {},
    field: {},
    textarea: {},
  },
  'bw-icons': {
    calendar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '1px',
      right: 0,
      bottom: '1px',
      width: '38px',
      height: '38px',
      p: 0,
      bg: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRadius: 0,
      cursor: 'pointer',
      outline: 0,
      transition: 'border 0.3s',

      'input.error ~ & svg': {
        stroke: 'error',
      },
      '& svg': {
        stroke: 'basic.3',
        transition: 'stroke 0.3s',
      },
      'input:disabled ~ & svg': {
        stroke: 'basic.3',
      },
      'input:disabled ~ &:hover': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
      },
      'input:disabled ~ &:active': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
        borderWidth: '1px',
      },
      'input:disabled ~ &:focus': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
        borderWidth: '1px',
      },
      '&:hover': {
        svg: {
          stroke: 'primary',
        },
      },
      '&:active': {
        svg: {
          stroke: 'primary',
        },
      },
      '&:focus': {
        svg: {
          stroke: 'primary',
        },
      },
    },
    plus: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '1px',
      right: 0,
      bottom: '1px',
      width: '38px',
      height: '38px',
      p: 0,
      bg: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRadius: 0,
      outline: 0,

      '&.error svg': {
        stroke: 'error',
      },
      '& svg': {
        stroke: 'basic.3',
        transition: 'stroke 0.3s',
      },
      '&:disabled svg': {
        stroke: 'basic.2',
      },
      '&:disabled:active svg': {
        stroke: 'basic.2',
      },
      '&:disabled:hover': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
      },
      '&:disabled:active': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
        borderWidth: '1px',
      },
      '&:disabled:focus': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
        borderWidth: '1px',
      },
      '&:hover': {
        cursor: 'pointer',

        svg: {
          stroke: 'primary',
        },
      },
      '&:active': {
        svg: {
          stroke: 'primary',
        },
      },
      '&:focus': {
        svg: {
          stroke: 'primary',
        },
      },
    },
    minus: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '1px',
      left: 0,
      bottom: '1px',
      width: '38px',
      height: '38px',
      p: 0,
      bg: 'transparent',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'transparent',
      borderRadius: 0,
      outline: 0,

      '&.error svg': {
        stroke: 'error',
      },
      '& svg': {
        stroke: 'basic.3',
        transition: 'stroke 0.3s',
      },
      '&:disabled svg': {
        stroke: 'basic.2',
      },
      '&:disabled:active svg': {
        stroke: 'basic.2',
      },
      '&:disabled:hover': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
      },
      '&:disabled:active': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
        borderWidth: '1px',
      },
      '&:disabled:focus': {
        cursor: 'not-allowed',
        borderColor: 'transparent',
        borderWidth: '1px',
      },
      '&:hover': {
        cursor: 'pointer',

        svg: {
          stroke: 'primary',
        },
      },
      '&:active': {
        svg: {
          stroke: 'primary',
        },
      },
      '&:focus': {
        svg: {
          stroke: 'primary',
        },
      },
    },
  },
  alerts: {
    'field-error': {
      fontSize: 0,
      lineHeight: 'caption',
      color: 'error',
    },
    'form-error': {
      mb: 3,
      py: 2,
      px: 3,
      fontSize: 1,
      textAlign: 'center',
      bg: 'error',
      color: 'basic.0',
    },
  },
};

export default theme;
